import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { schoolClassesApi } from '../school-classes';

export const schoolClassesKeys = ['admin', 'timetable', 'classes'] as const;

export function useSchoolClassesQuery(schoolYearId: string | undefined) {
  return useQuery({
    queryKey: [...schoolClassesKeys, schoolYearId] as const,
    queryFn: () => schoolClassesApi.list(schoolYearId!),
    enabled: Boolean(schoolYearId),
  });
}

/** No year named means the active one, which a teacher (who cannot list years) can still read. */
export function useActiveYearSchoolClassesQuery(enabled = true) {
  return useQuery({
    queryKey: [...schoolClassesKeys, 'active'] as const,
    queryFn: () => schoolClassesApi.list(''),
    enabled,
  });
}

export function useCreateSchoolClassMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof schoolClassesApi.create>[0]) => schoolClassesApi.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: schoolClassesKeys }),
  });
}

export function useUpdateSchoolClassMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof schoolClassesApi.update>[1] }) =>
      schoolClassesApi.update(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: schoolClassesKeys }),
  });
}

export function useDeleteSchoolClassMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => schoolClassesApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: schoolClassesKeys }),
  });
}

export function useEnsureGradesMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (schoolYearId: string) => schoolClassesApi.ensureGrades(schoolYearId),
    onSuccess: () => qc.invalidateQueries({ queryKey: schoolClassesKeys }),
  });
}
